/**
 * 학부모 대시보드 데모 페이지
 * 인증 없이 목업 데이터로 대시보드를 보여줌 (고객 시연용)
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Drawer,
  useMediaQuery,
  useTheme,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment';
import SettingsIcon from '@mui/icons-material/Settings';

const DRAWER_WIDTH = 260;

// 데모 데이터
const DEMO_PARENT = { name: '김영희', phone: '010-1234-5678' };
const DEMO_CHILDREN = [
  {
    id: '1',
    name: '김민준',
    ticket: {
      type: '30일 정기권',
      status: 'active',
      expires_at: (() => {
        const d = new Date();
        d.setDate(d.getDate() + 22);
        return d.toISOString();
      })(),
      remaining_hours: null,
      days_left: 22,
    },
  },
  {
    id: '2',
    name: '김서연',
    ticket: {
      type: '30일 정기권',
      status: 'active',
      expires_at: (() => {
        const d = new Date();
        d.setDate(d.getDate() + 2);
        return d.toISOString();
      })(),
      remaining_hours: null,
      days_left: 2,
    },
  },
];
const DEMO_UNREAD_NOTICES = 3;

const SIDEBAR_ITEMS = [
  { icon: <DashboardIcon />, label: '대시보드', active: true },
  { icon: <PeopleIcon />, label: '자녀 관리', active: false },
  { icon: <EventNoteIcon />, label: '출결 현황', active: false },
  { icon: <AccessTimeIcon />, label: '학습 시간', active: false },
  { icon: <PaymentIcon />, label: '결제', active: false },
  { icon: <NotificationsIcon />, label: '알림장', active: false },
  { icon: <SettingsIcon />, label: '설정', active: false },
];

export default function DemoPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const formatExpiryDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  type StatusColor = 'success' | 'warning' | 'error' | 'default';
  const getTicketStatusColor = (ticket: typeof DEMO_CHILDREN[0]['ticket']): StatusColor => {
    if (ticket.status === 'none') return 'default';
    if (ticket.days_left !== null && ticket.days_left <= 3) return 'warning';
    return 'success';
  };

  const getTicketDescription = (ticket: typeof DEMO_CHILDREN[0]['ticket']): string => {
    if (ticket.status === 'none') return '이용권 없음';
    const parts: string[] = [ticket.type];
    if (ticket.expires_at) {
      parts.push(`${formatExpiryDate(ticket.expires_at)} 만료`);
      if (ticket.days_left !== null) {
        if (ticket.days_left > 0) parts.push(`D-${ticket.days_left}`);
        else if (ticket.days_left === 0) parts.push('오늘 만료');
        else parts.push('만료됨');
      }
    }
    if (ticket.remaining_hours !== null) {
      parts.push(`잔여 ${ticket.remaining_hours}시간`);
    }
    return parts.join(' | ');
  };

  const sidebar = (
    <Box sx={{ overflow: 'auto' }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={700} color="primary">뉴어텐드</Typography>
        <Typography variant="caption" color="text.secondary">학부모 포털</Typography>
      </Box>
      <Divider />
      <List>
        {SIDEBAR_ITEMS.map((item) => (
          <ListItem
            key={item.label}
            sx={{
              bgcolor: item.active ? 'primary.50' : 'transparent',
              borderRight: item.active ? '3px solid' : 'none',
              borderColor: 'primary.main',
              cursor: 'default',
            }}
          >
            <ListItemIcon sx={{ color: item.active ? 'primary.main' : 'text.secondary' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: item.active ? 600 : 400,
                color: item.active ? 'primary.main' : 'text.primary',
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* 상단 앱바 */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={700} color="primary">뉴어텐드</Typography>
            <Typography variant="subtitle2" color="text.secondary">학부모</Typography>
            <Chip label="DEMO" size="small" color="info" sx={{ ml: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '14px' }}>
              {DEMO_PARENT.name[0]}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" fontWeight={600}>{DEMO_PARENT.name}</Typography>
              <Typography variant="caption" color="text.secondary">{DEMO_PARENT.phone}</Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 사이드바 */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
          >
            {sidebar}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
            open
          >
            {sidebar}
          </Drawer>
        )}
      </Box>

      {/* 메인 콘텐츠 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '64px',
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {/* 인사말 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            안녕하세요, {DEMO_PARENT.name}님
          </Typography>
        </Box>

        {/* 이용권 현황 */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ConfirmationNumberIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>이용권 현황</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {DEMO_CHILDREN.map((child) => {
                const statusColor = getTicketStatusColor(child.ticket);
                const isExpiringSoon = child.ticket.days_left !== null && child.ticket.days_left <= 3 && child.ticket.days_left >= 0;

                return (
                  <Box
                    key={child.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: statusColor === 'warning' ? 'warning.50' : 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: statusColor === 'warning' ? 'warning.200' : 'grey.200',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>{child.name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        {isExpiringSoon && <WarningAmberIcon fontSize="small" color="warning" />}
                        <Typography variant="body2" color="text.secondary">
                          {getTicketDescription(child.ticket)}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={child.ticket.days_left !== null && child.ticket.days_left <= 0 ? '만료' : '이용중'}
                      color={statusColor}
                      size="small"
                    />
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>

        {/* 알림장 */}
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>알림장</Typography>
                <Chip label={`${DEMO_UNREAD_NOTICES}개 안읽음`} color="error" size="small" />
              </Box>
              <Button variant="text" endIcon={<ArrowForwardIcon />}>바로가기</Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
